import { useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";

/**
 * Realtime messaging hook for the recruiter dashboard.
 *
 * Subscribes to:
 * 1. postgres_changes on messages table (INSERT + UPDATE) filtered by recruiter_id
 * 2. Broadcast channel per conversation for typing indicators
 *
 * Includes reconnection fallback to fetch missed messages.
 *
 * Uses refs for callbacks so the Supabase channel stays stable and
 * only re-subscribes when recruiterId changes (not on every render).
 */
export function useRealtimeMessages({
  recruiterId,
  activeCandidateId,
  onNewMessage,
  onMessageUpdate,
  onTypingChange,
  onUnreadIncrement,
  onReconnect,
}) {
  const typingChannelRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingBroadcastRef = useRef(0);

  // Stable refs for callbacks — prevents channel teardown on every render
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageUpdateRef = useRef(onMessageUpdate);
  const onReconnectRef = useRef(onReconnect);
  const onTypingChangeRef = useRef(onTypingChange);

  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { onMessageUpdateRef.current = onMessageUpdate; }, [onMessageUpdate]);
  useEffect(() => { onReconnectRef.current = onReconnect; }, [onReconnect]);
  useEffect(() => { onTypingChangeRef.current = onTypingChange; }, [onTypingChange]);

  // 1. Postgres Changes — new messages + read receipts
  useEffect(() => {
    if (!recruiterId) return;

    const channel = supabase
      .channel(`messages:recruiter:${recruiterId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `recruiter_id=eq.${recruiterId}`,
        },
        (payload) => {
          onNewMessageRef.current(payload.new);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `recruiter_id=eq.${recruiterId}`,
        },
        (payload) => {
          const msg = payload.new;
          if (msg.read_at && msg.sender_role === "recruiter") {
            onMessageUpdateRef.current(msg);
          }
        }
      )
      .on("system", {}, (payload) => {
        // Reconnection fallback — fetch missed messages
        if (payload.extension === "postgres_changes" && payload.status === "ok") {
          onReconnectRef.current?.();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [recruiterId]);

  // 2. Broadcast channel for typing indicators (per active conversation)
  useEffect(() => {
    if (!recruiterId || !activeCandidateId) {
      if (typingChannelRef.current) {
        supabase.removeChannel(typingChannelRef.current);
        typingChannelRef.current = null;
      }
      return;
    }

    const conversationKey = `recruiter:${recruiterId}_candidate:${activeCandidateId}`;
    const channel = supabase
      .channel(`chat:${conversationKey}`)
      .on("broadcast", { event: "typing" }, (payload) => {
        if (payload.payload?.sender_role === "student") {
          onTypingChangeRef.current(true);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => onTypingChangeRef.current(false), 4000);
        }
      })
      .subscribe();

    typingChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      typingChannelRef.current = null;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [recruiterId, activeCandidateId]);

  // 3. Broadcast own typing signal (debounced, max once per 2s)
  const broadcastTyping = useCallback(() => {
    if (!typingChannelRef.current) return;
    const now = Date.now();
    if (now - lastTypingBroadcastRef.current < 2000) return;
    lastTypingBroadcastRef.current = now;

    typingChannelRef.current.send({
      type: "broadcast",
      event: "typing",
      payload: { sender_role: "recruiter" },
    });
  }, []);

  return { broadcastTyping };
}
